import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    addToCart(product);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const getConditionLabel = (condition) => {
    return condition === 'new' ? 'NOVO' : 'SEMINOVO';
  };

  // Calcula o menor preço entre todas as variações (storage_variants)
  const { minPrice, hasVariants, priceRange } = useMemo(() => {
    // Parse storage_variants se for string
    let storageVariants = product.storage_variants;
    if (typeof storageVariants === 'string') {
      try {
        storageVariants = JSON.parse(storageVariants);
      } catch {
        storageVariants = [];
      }
    }

    // Se tem storage_variants com preços definidos
    if (storageVariants && storageVariants.length > 0) {
      const prices = storageVariants.map(sv => sv.price).filter(p => p > 0);
      if (prices.length > 0) {
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        return {
          minPrice: min,
          hasVariants: true,
          priceRange: { min, max, hasRange: min !== max }
        };
      }
    }

    // Fallback para o preço base do produto
    const basePrice = product.price_pix || product.price || 0;
    return {
      minPrice: basePrice,
      hasVariants: false,
      priceRange: { min: basePrice, max: basePrice, hasRange: false }
    };
  }, [product]);

  // Verifica se tem estoque (considerando storage_variants)
  const hasStock = useMemo(() => {
    let storageVariants = product.storage_variants;
    if (typeof storageVariants === 'string') {
      try {
        storageVariants = JSON.parse(storageVariants);
      } catch {
        storageVariants = [];
      }
    }

    // Se tem storage_variants, verifica se algum tem estoque
    if (storageVariants && storageVariants.length > 0) {
      return storageVariants.some(sv => sv.stock === undefined || sv.stock > 0);
    }

    // Fallback para estoque do produto
    return product.stock > 0;
  }, [product]);

  return (
    <Link 
      to={`/produto/${product.id}`}
      className="group relative bg-[#2A2A2D] rounded-lg overflow-hidden border border-[#3A3A3D] hover:border-[#C9A24D] transition-all duration-500 flex flex-col h-full shadow-lg hover:shadow-[0_8px_30px_rgba(201,162,77,0.3)] transform hover:-translate-y-1"
      data-testid={`product-card-${product.id}`}
    >
      {/* Featured Badge */}
      {product.featured && (
        <div className="absolute top-3 left-3 z-10 bg-[#C9A24D] text-[#0F0F10] px-3 py-1 rounded text-xs font-bold uppercase tracking-wider glow-gold">
          Destaque
        </div>
      )}

      {/* Condition Badge */}
      <div className="absolute top-3 right-3 z-10">
        <span className="px-2 py-1 bg-[#1C1C1F] border border-[#C9C9C9] text-[#C9C9C9] rounded text-xs font-semibold uppercase tracking-wider">
          {getConditionLabel(product.condition)}
        </span>
      </div>

      {/* Product Image */}
      <div className="relative h-56 bg-[#1C1C1F] overflow-hidden">
        {product.images && product.images.length > 0 ? (
          <img
            src={product.images[0]}
            alt={`${product.brand} ${product.model}`}
            className="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-[#3A3A3D] text-center">
              <ShoppingCart className="h-16 w-16 mx-auto mb-2" />
              <p className="text-xs">Sem imagem</p>
            </div>
          </div>
        )}
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A2A2D] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      </div>

      {/* Product Info */}
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="text-cinzel font-semibold text-[#EAEAEA] text-base mb-1 tracking-wide">
          {product.brand || product.title}
        </h3>
        <p className="text-[#B5B5B5] text-sm mb-3">{product.model}</p>

        {/* Original Price (strikethrough) - só mostra se não tem variantes */}
        {!hasVariants && product.market_price && product.market_price > minPrice && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-[#666] line-through">
              {formatPrice(product.market_price)}
            </span>
            <span className="bg-[#C9A24D] text-[#0F0F10] px-2 py-0.5 rounded text-xs font-bold">
              -{Math.round((1 - minPrice / product.market_price) * 100)}%
            </span>
          </div>
        )}

        {/* Price Section */}
        <div className="mb-4">
          {/* PIX Price - Destacado */}
          <div className="bg-[#1C1C1F] border border-[#C9A24D] rounded-lg p-3 mb-2">
            <div className="flex flex-col">
              {/* "A partir de" label para produtos com variações */}
              {hasVariants && priceRange.hasRange && (
                <p className="text-xs text-[#B5B5B5] mb-0.5 uppercase tracking-wider">
                  A partir de
                </p>
              )}
              <p className="text-[#C9A24D] font-bold text-xl tracking-wide">
                {formatPrice(minPrice)}
              </p>
              <p className="text-xs text-[#B5B5B5] font-semibold uppercase tracking-wider">
                no PIX
              </p>
            </div>
          </div>

          {/* Installment - só mostra se não tem variantes (preço fixo) */}
          {!hasVariants && (
            <p className="text-xs text-[#B5B5B5] text-center">
              ou <span className="font-semibold text-[#EAEAEA]">{formatPrice(minPrice * 1.1)}</span> em 10x
            </p>
          )}
          
          {/* Para produtos com variantes, mostra range de preço */}
          {hasVariants && priceRange.hasRange && (
            <p className="text-xs text-[#666] text-center mt-1">
              até {formatPrice(priceRange.max)}
            </p>
          )}
        </div>

        {/* Stock */}
        <div className="mb-4">
          {hasStock ? (
            <p className="text-[#C9A24D] text-xs font-medium uppercase tracking-wider">
              ✓ Disponível
            </p>
          ) : (
            <p className="text-[#666] text-xs font-medium uppercase tracking-wider">
              ✗ Indisponível
            </p>
          )}
        </div>

        {/* Button */}
        <div className="mt-auto">
          <button
            onClick={handleAddToCart}
            disabled={!hasStock}
            className={`w-full py-3 rounded-lg font-bold text-sm uppercase tracking-wider transition-all duration-300 ${
              hasStock
                ? 'bg-[#C9A24D] text-[#0F0F10] hover:bg-[#E0B85C] glow-gold hover:glow-gold-strong'
                : 'bg-[#3A3A3D] text-[#666] cursor-not-allowed'
            }`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {hasStock ? 'Ver Detalhes' : 'Indisponível'}
          </button>
        </div>
      </div>

      {/* Bottom Border Glow on Hover */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A24D] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    </Link>
  );
};

export default ProductCard;
